/**
 * CenterDashboardPage — owner/operator dashboard for the user's center.
 * Mirrors Flutter `center_dashboard_page.dart`. If the user has no center
 * yet → show a "register your center" CTA.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2, Star, MapPin, Phone, Clock, AlertCircle, Loader,
  Package, Users, Pause, Play, Plus,
} from 'lucide-react';
import { useMyCenters, useResumeCenter } from '@/hooks/useCenters';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import type { Center } from '@/types/center';
import { isCenterPaused } from '@/types/center';

const STATUS_LABEL: Record<Center['status'], { label: string; color: string }> = {
  pending:  { label: 'قيد المراجعة', color: '#F59E0B' },
  approved: { label: 'معتمَد ✓',     color: '#16A34A' },
  rejected: { label: 'مرفوض',        color: '#DC2626' },
};

export default function CenterDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: centers, isLoading } = useMyCenters(true);
  const resumeMut = useResumeCenter();

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const c = centers?.[0];

  // No center yet → registration CTA
  if (!c) {
    return (
      <AppLayout>
        <div className="bg-surface border border-border rounded-2xl p-10 text-center max-w-lg mx-auto">
          <Building2 size={56} className="mx-auto text-primary mb-4" />
          <h1 className="text-xl font-bold text-text-primary mb-2">
            {t('no_center_yet', 'سجِّل مركز شحن')}
          </h1>
          <p className="text-text-secondary mb-5">
            {t('register_center_pitch', 'حوّل محلك أو مكتبك لنقطة استلام/تسليم وابدأ كسب عمولة على كل شحنة تمر منه.')}
          </p>
          <Link
            to="/center/register"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-xl px-6 py-3 text-sm font-semibold hover:bg-primary-light"
          >
            <Plus size={16} /> {t('register_center', 'تسجيل مركز شحن')}
          </Link>
        </div>
      </AppLayout>
    );
  }

  const statusCfg = STATUS_LABEL[c.status];
  const paused = isCenterPaused(c);

  return (
    <AppLayout>
      {/* Header card */}
      <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={18} className="text-primary" />
              <h1 className="text-xl font-bold text-text-primary">{c.name}</h1>
            </div>
            <div className="flex items-center gap-2 text-text-secondary text-sm">
              <MapPin size={14} /> {c.address}
            </div>
          </div>
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: `${statusCfg.color}22`, color: statusCfg.color }}
          >
            {t(`center_status.${c.status}`, statusCfg.label)}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-text-secondary">
          {c.phone && (
            <span className="flex items-center gap-1.5"><Phone size={14} /> <span dir="ltr">{c.phone}</span></span>
          )}
          {c.operating_hours && typeof c.operating_hours === 'string' && (
            <span className="flex items-center gap-1.5"><Clock size={14} /> {c.is_24_hours ? '٢٤ ساعة' : c.operating_hours}</span>
          )}
          {c.rating_avg != null && (
            <span className="flex items-center gap-1.5"><Star size={14} className="fill-warning text-warning" /> {c.rating_avg.toFixed(1)} ({c.rating_count || 0})</span>
          )}
        </div>
      </div>

      {/* Pause banner */}
      {paused && (
        <div className="bg-warning/10 border border-warning/30 rounded-2xl p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-warning">
            <Pause size={18} />
            <div>
              <div className="font-bold">{t('center_paused', 'المركز متوقّف مؤقتاً')}</div>
              {c.pause_reason && <div className="text-sm">{c.pause_reason}</div>}
            </div>
          </div>
          <Button onClick={() => resumeMut.mutate(c.id)} loading={resumeMut.isPending}>
            <Play size={14} /> {t('resume', 'استئناف')}
          </Button>
        </div>
      )}

      {/* Rejection */}
      {c.status === 'rejected' && c.rejection_reason && (
        <div className="bg-error/10 border border-error/30 rounded-2xl p-4 mb-4 flex items-start gap-2 text-error">
          <AlertCircle size={18} className="mt-0.5" />
          <div>
            <div className="font-bold">{t('rejected_reason', 'سبب الرفض')}</div>
            <div className="text-sm">{c.rejection_reason}</div>
            <Link to={`/center/edit`} className="text-error underline text-sm mt-1 inline-block">
              {t('fix_and_resubmit', 'تعديل وإعادة التقديم')}
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Stat icon={Package} label={t('active_shipments', 'الشحنات النشطة')} value={c.active_shipments_count ?? 0} color="#0F2A5C" />
        <Stat icon={Package} label={t('total_shipments', 'الإجمالي التاريخي')} value={c.total_shipments_count ?? 0} color="#16A34A" />
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ActionTile to="/center/shipments" icon={Package}  label={t('center_inventory', 'المخزون')} />
        <ActionTile
          to="/center/create-for-customer"
          icon={Plus}
          label={t('create_for_customer_tile', 'إنشاء شحنة لعميل')}
        />
        <ActionTile
          to="/center/delegated-shipments"
          icon={Package}
          label={t('delegated_shipments_tile', 'شحناتي لعملاء')}
        />
        <ActionTile to="/center/operators" icon={Users}    label={t('operators', 'الموظفون')} />
        <ActionTile to="/center/edit"      icon={Building2} label={t('edit_center', 'تعديل البيانات')} />
        <ActionTile to="/earnings"         icon={Star}     label={t('earnings', 'الأرباح')} onClick={() => navigate('/earnings')} />
      </div>
    </AppLayout>
  );
}

function Stat({ icon: Icon, label, value, color }: { icon: typeof Package; label: string; value: number; color: string }) {
  return (
    <div className="bg-surface border border-border border-t-4 rounded-xl p-4" style={{ borderTopColor: color }}>
      <div className="flex items-center gap-2 text-text-tertiary text-xs mb-1">
        <Icon size={14} /> {label}
      </div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function ActionTile({ to, icon: Icon, label, onClick }: { to: string; icon: typeof Package; label: string; onClick?: () => void }) {
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="bg-surface border border-border rounded-xl p-4 flex flex-col items-start gap-2 hover:border-primary transition text-start"
      >
        <Icon size={22} className="text-primary" />
        <span className="font-semibold text-sm text-text-primary">{label}</span>
      </button>
    );
  }
  return (
    <Link
      to={to}
      className="bg-surface border border-border rounded-xl p-4 flex flex-col items-start gap-2 hover:border-primary transition"
    >
      <Icon size={22} className="text-primary" />
      <span className="font-semibold text-sm text-text-primary">{label}</span>
    </Link>
  );
}
